import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import { Calendar, Download, Filter, Search } from 'lucide-react'
import { useState } from 'react'

import { PageLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/report/journal')({
  loader: () => {
    return {
      crumb: 'Laporan Transaksi',
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <PageLayout>
      <PageLayout.Header title='Laporan Transaksi' />
      <TransactionReportPage />
    </PageLayout>
  )
}

interface Transaction {
  id: string
  date: string
  ref: string
  type: 'sale' | 'purchase' | 'transfer' | 'production' | 'cash_in' | 'cash_out' | 'payroll'
  outletId: 'ikki_coffee' | 'ikki_resto'
  description: string
  entries: {
    accountCode: string
    accountName: string
    debit: number
    credit: number
  }[]
  totalDebit: number
  totalCredit: number
}

const OUTLETS = [
  { id: 'ikki_coffee', name: 'Ikki Coffee' },
  { id: 'ikki_resto', name: 'Ikki Resto' },
]

const TRANSACTION_TYPES = [
  { value: 'all', label: 'Semua Transaksi' },
  { value: 'sale', label: 'Penjualan' },
  { value: 'purchase', label: 'Pembelian' },
  { value: 'transfer', label: 'Transfer Stok' },
  { value: 'production', label: 'Produksi' },
  { value: 'payroll', label: 'Payroll' },
]

const mockTransactions: Transaction[] = [
  {
    id: 'jrn_001',
    date: '2025-11-03T12:30:00Z',
    ref: 'POS-20251103-001',
    type: 'sale',
    outletId: 'ikki_coffee',
    description: 'Penjualan Latte & Cappuccino',
    entries: [
      { accountCode: '10100', accountName: 'Kas di Tangan', debit: 50000, credit: 0 },
      { accountCode: '40100', accountName: 'Penjualan Tunai', debit: 0, credit: 45045 },
      { accountCode: '20200', accountName: 'PPN Keluaran', debit: 0, credit: 4955 },
      { accountCode: '50010', accountName: 'HPP', debit: 24000, credit: 0 },
      { accountCode: '10300', accountName: 'Persediaan Bahan Baku', debit: 0, credit: 24000 },
    ],
    totalDebit: 74000,
    totalCredit: 74000,
  },
  {
    id: 'jrn_002',
    date: '2025-11-03T14:15:00Z',
    ref: 'PUR-20251103-001',
    type: 'purchase',
    outletId: 'ikki_coffee',
    description: 'Pembelian Biji Kopi Arabica 10kg',
    entries: [
      { accountCode: '10300', accountName: 'Persediaan Bahan Baku', debit: 850000, credit: 0 },
      { accountCode: '10500', accountName: 'PPN Masukan', debit: 93500, credit: 0 },
      { accountCode: '20100', accountName: 'Hutang Usaha', debit: 0, credit: 943500 },
    ],
    totalDebit: 943500,
    totalCredit: 943500,
  },
  {
    id: 'jrn_003',
    date: '2025-11-03T16:00:00Z',
    ref: 'TRF-20251103-001',
    type: 'transfer',
    outletId: 'ikki_resto',
    description: 'Transfer Susu UHT 50 liter ke Ikki Resto',
    entries: [
      {
        accountCode: '10300',
        accountName: 'Persediaan Bahan Baku (Resto)',
        debit: 125000,
        credit: 0,
      },
      {
        accountCode: '10300',
        accountName: 'Persediaan Bahan Baku (Coffee)',
        debit: 0,
        credit: 125000,
      },
    ],
    totalDebit: 125000,
    totalCredit: 125000,
  },
  {
    id: 'jrn_004',
    date: '2025-11-04T09:00:00Z',
    ref: 'PROD-20251104-001',
    type: 'production',
    outletId: 'ikki_coffee',
    description: 'Produksi 50 gelas Latte',
    entries: [
      { accountCode: '10310', accountName: 'Barang Jadi (Latte)', debit: 600000, credit: 0 },
      { accountCode: '10300', accountName: 'Persediaan Bahan Baku', debit: 0, credit: 600000 },
    ],
    totalDebit: 600000,
    totalCredit: 600000,
  },
  {
    id: 'jrn_005',
    date: '2025-11-04T17:30:00Z',
    ref: 'PAY-20251104-001',
    type: 'payroll',
    outletId: 'ikki_resto',
    description: 'Gaji Karyawan Bulan November',
    entries: [
      { accountCode: '60100', accountName: 'Gaji Karyawan', debit: 15000000, credit: 0 },
      { accountCode: '60120', accountName: 'BPJS', debit: 750000, credit: 0 },
      { accountCode: '20200', accountName: 'PPh 21', debit: 0, credit: 1125000 },
      { accountCode: '20300', accountName: 'Hutang Gaji', debit: 0, credit: 14625000 },
    ],
    totalDebit: 15750000,
    totalCredit: 15750000,
  },
]

function TransactionReportPage() {
  const [outletId, setOutletId] = useState<string>('ikki_coffee')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const filteredTransactions = mockTransactions
    .filter(t => outletId === 'all' || t.outletId === outletId)
    .filter(t => typeFilter === 'all' || t.type === typeFilter)
    .filter(
      t =>
        t.ref.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase())
    )

  const getTypeBadge = (type: Transaction['type']) => {
    const variants: Record<
      Transaction['type'],
      { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
    > = {
      sale: { label: 'Penjualan', variant: 'default' },
      purchase: { label: 'Pembelian', variant: 'secondary' },
      transfer: { label: 'Transfer', variant: 'outline' },
      production: { label: 'Produksi', variant: 'default' },
      cash_in: { label: 'Penerimaan', variant: 'secondary' },
      cash_out: { label: 'Pengeluaran', variant: 'destructive' },
      payroll: { label: 'Payroll', variant: 'destructive' },
    }
    const { label, variant } = variants[type]
    return <Badge variant={variant}>{label}</Badge>
  }

  return (
    <div>
      {/* Header */}
      <div
        className={`
          mb-6 rounded-lg bg-white p-6 shadow-sm
          dark:bg-gray-800
        `}
      >
        <div
          className={`
            flex flex-col items-start justify-between gap-4
            md:flex-row md:items-center
          `}
        >
          <div>
            <h1
              className={`
                text-2xl font-bold text-gray-900
                dark:text-white
              `}
            >
              Laporan Transaksi
            </h1>
            <p
              className={`
                mt-1 text-sm text-gray-500
                dark:text-gray-400
              `}
            >
              Jurnal akuntansi per outlet
            </p>
          </div>
          <Button variant='outline' size='sm'>
            <Download className='mr-2 h-4 w-4' />
            Export Excel
          </Button>
        </div>

        {/* Filters */}
        <div
          className={`
            mt-6 grid grid-cols-1 gap-4
            md:grid-cols-3
          `}
        >
          <div>
            <Label htmlFor='outlet'>Outlet</Label>
            <Select value={outletId} onValueChange={setOutletId}>
              <SelectTrigger id='outlet'>
                <SelectValue placeholder='Pilih outlet' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>Semua Outlet</SelectItem>
                {OUTLETS.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor='type'>Tipe Transaksi</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger id='type'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSACTION_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor='search'>Cari</Label>
            <div className='relative'>
              <Search className='absolute top-3 left-3 h-4 w-4 text-gray-400' />
              <Input
                id='search'
                placeholder='Ref / Deskripsi...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='pl-10'
              />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Daftar Jurnal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='overflow-x-auto'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Tipe</TableHead>
                  <TableHead>Deskripsi</TableHead>
                  <TableHead className='text-right'>Debit</TableHead>
                  <TableHead className='text-right'>Kredit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map(txn => (
                  <TableRow
                    key={txn.id}
                    className={`
                      hover:bg-gray-50
                      dark:hover:bg-gray-800
                    `}
                  >
                    <TableCell className='font-medium'>
                      {format(new Date(txn.date), 'dd MMM yyyy HH:mm', { locale: id })}
                    </TableCell>
                    <TableCell>{txn.ref}</TableCell>
                    <TableCell>{getTypeBadge(txn.type)}</TableCell>
                    <TableCell className='max-w-xs truncate'>
                      <div className='space-y-1'>
                        <p className='font-medium'>{txn.description}</p>
                        <div className='space-y-0.5 text-xs text-gray-500'>
                          {txn.entries.map((e, i) => (
                            <div key={i} className='flex justify-between'>
                              <span>
                                {e.accountCode} - {e.accountName}
                              </span>
                              <span className='font-mono'>
                                {e.debit > 0 ? toIdr(e.debit) : toIdr(e.credit)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className='text-right font-mono text-green-600'>
                      {toIdr(txn.totalDebit)}
                    </TableCell>
                    <TableCell className='text-right font-mono text-red-600'>
                      {toIdr(txn.totalCredit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredTransactions.length === 0 && (
            <div className='py-12 text-center text-gray-500'>Tidak ada transaksi ditemukan</div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
